import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Smartphone, Tablet, Monitor, Activity, Eye, Clock, BellRing, Send } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, handleErr } from "./AdminUI";

const Kpi = ({ icon: I, label, value, testid }) => (
  <Card className="p-4" data-testid={testid}><div className="flex items-center gap-2 text-xs text-slate-400"><I className="w-4 h-4 text-[#E11D2A]" />{label}</div>
    <div className="text-2xl font-extrabold text-white mt-1 tabular-nums">{value}</div></Card>
);
const List = ({ title, rows, k, v }) => (
  <Card className="p-4"><div className="text-sm font-bold text-white mb-2">{title}</div>
    {rows.length ? rows.map((r) => <div key={r[k]} className="flex justify-between text-sm py-1 border-t border-[#273042]/60 first:border-0"><span className="text-slate-300 truncate">{r[k] || "—"}</span><span className="text-white tabular-nums">{r[v]}</span></div>)
      : <p className="text-xs text-slate-500">No data yet</p>}</Card>
);

function PushPanel() {
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ title: "", body: "", url: "/", category: "promotions" });
  const load = () => endpoints.adminPushStats().then((r) => setSt(r.data)).catch(handleErr);
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!window.confirm(`Send this push to every opted-in device (${st?.subscriptions || 0})?`)) return;
    try { const { data } = await endpoints.adminPushSend(f); toast.success(data.stopped ? "Push is stopped in API Management" : `Sent to ${data.sent} device(s) · ${data.failed} failed · ${data.expired} expired removed`); load(); }
    catch (e) { handleErr(e); }
  };
  const inp = "h-10 w-full rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none";
  return (
    <Card className="p-5 mt-6" data-testid="admin-push-panel">
      <div className="text-white font-bold flex items-center gap-2"><BellRing className="w-5 h-5 text-[#E11D2A]" /> App notifications (push)</div>
      {st && <p className="text-xs text-slate-400 mt-1" data-testid="push-sub-count">{st.subscriptions} opted-in device(s) · {st.members} members · {st.guests} guests · {Object.entries(st.byPlatform).map(([p, n]) => `${p}: ${n}`).join(" · ")}</p>}
      <p className="text-[11px] text-slate-500 mt-1">Automatic alerts go out when a programme goes LIVE, a new video is published or an event goes on sale.</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        <input className={inp} placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="push-title" />
        <input className={inp} placeholder="Link (e.g. /live)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="push-url" />
        <input className={`${inp} sm:col-span-2`} placeholder="Message" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="push-body" />
      </div>
      <Btn onClick={send} disabled={!f.title.trim()} className="mt-3" data-testid="push-send"><Send className="w-4 h-4" /> Send push to all opted-in</Btn>
      {st?.recent?.length > 0 && <div className="mt-4 space-y-1">{st.recent.map((n, i) => (
        <div key={i} className="text-xs text-slate-400 flex justify-between border-t border-[#273042]/60 pt-1"><span className="truncate">{n.title}</span>
          <span className="shrink-0 ml-2">{(n.createdAt || "").slice(0, 16).replace("T", " ")} · {n.stats?.sent ?? 0}/{n.stats?.recipients ?? 0}</span></div>))}</div>}
    </Card>
  );
}

export default function AdminVisitors() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => endpoints.adminVisitors(days).then((r) => setD(r.data)).catch(handleErr);
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [days]);
  const max = Math.max(1, ...(d?.daily || []).map((x) => x.visitors));
  return (
    <div data-testid="admin-visitors">
      <PageHeader title="Visitors & Notifications" desc="Anonymous visit analytics (no names, no location): phones, tablets and computers.">
        {[1, 7, 30, 90].map((n) => <Btn key={n} variant={days === n ? "primary" : "ghost"} onClick={() => setDays(n)} data-testid={`visitors-days-${n}`}>{n === 1 ? "Today" : `${n} days`}</Btn>)}
      </PageHeader>
      {d && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          <Kpi icon={Activity} label="Active now (5 min)" value={d.activeNow} testid="kpi-active" />
          <Kpi icon={Users} label="Unique visitors" value={d.uniqueVisitors} testid="kpi-unique" />
          <Kpi icon={Eye} label="Page views" value={d.pageViews} testid="kpi-views" />
          <Kpi icon={Users} label="Sessions" value={d.sessions} testid="kpi-sessions" />
          <Kpi icon={Users} label="New / returning" value={`${d.newVisitors} / ${d.returningVisitors}`} testid="kpi-new" />
          <Kpi icon={Clock} label="Avg session" value={`${Math.floor(d.avgSessionSec / 60)}m ${d.avgSessionSec % 60}s`} testid="kpi-duration" />
          <Kpi icon={Smartphone} label="Phones" value={d.devices.phone} testid="kpi-phone" />
          <Kpi icon={Tablet} label="Tablets" value={d.devices.tablet} testid="kpi-tablet" />
          <Kpi icon={Monitor} label="Computers" value={d.devices.computer} testid="kpi-computer" />
          <Kpi icon={Users} label="Today / 7 days" value={`${d.todayVisitors} / ${d.weekVisitors}`} testid="kpi-dau" />
          <Kpi icon={Activity} label="Bounce rate" value={`${d.bounceRate}%`} testid="kpi-bounce" />
          <Kpi icon={Users} label="Signed-in members" value={d.signedInMembers} testid="kpi-members" />
        </div>
        <Card className="p-4 mt-4" data-testid="visitors-daily">
          <div className="text-sm font-bold text-white mb-3">Visitors per day</div>
          <div className="flex items-end gap-1 h-32 overflow-x-auto">{d.daily.map((x) => (
            <div key={x.day} className="flex flex-col items-center gap-1 shrink-0" title={`${x.day}: ${x.visitors} visitors, ${x.views} views`}>
              <div className="w-4 rounded-t bg-[#E11D2A]/80" style={{ height: `${Math.max(3, (x.visitors / max) * 110)}px` }} /><span className="text-[9px] text-slate-500">{x.day.slice(8)}</span></div>))}</div>
        </Card>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
          <List title="Top pages" rows={d.topPages} k="path" v="views" />
          <List title="Operating systems" rows={d.os} k="name" v="visitors" />
          <List title="Browsers" rows={d.browsers} k="name" v="visitors" />
          <List title="Referrers" rows={d.referrers} k="ref" v="views" />
        </div>
      </>)}
      <PushPanel />
      <ClientErrors />
    </div>
  );
}

function ClientErrors() {
  const [rows, setRows] = useState(null);
  useEffect(() => { endpoints.adminClientErrors().then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <Card className="p-4 mt-4" data-testid="client-errors-card">
      <div className="text-sm font-bold text-white mb-1">App errors on members' devices</div>
      <p className="text-xs text-slate-400 mb-3">Crashes caught by the safety screen (last 14 days). Empty is good.</p>
      {!rows ? <div className="text-xs text-slate-500">Loading…</div> : rows.length === 0 ? <div className="text-xs text-emerald-400" data-testid="client-errors-empty">No app errors reported.</div> : (
        <div className="max-h-72 overflow-y-auto space-y-2">{rows.map((r, i) => (
          <div key={i} className="rounded-lg bg-[#0A0C10] border border-[#273042] p-2.5 text-xs" data-testid={`client-error-${i}`}>
            <div className="text-red-300 font-mono break-all">{r.message}</div>
            <div className="text-slate-500 mt-1 break-all">{new Date(r.ts).toLocaleString()} · {r.path} · {r.ua?.slice(0, 120)}</div>
          </div>))}</div>
      )}
    </Card>
  );
}
